import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, type FormEvent } from "react";
import { Mail, Leaf, CheckCircle2, Loader2 } from "lucide-react";
import heroAsset from "@/assets/speaker-hero-v2.webp.asset.json";


const heroImage = heroAsset.url;

const TRACK_SCRIPT_BASE = "https://kegqh.ttrk.io/track.js";
const TRACK_CMP_ID = "6a321a03014cc93d3a537df0";
const PAGEVIEW_POSTBACK_BASE = "https://kegqh.ttrk.io/postback";

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}
function setCookie(name: string, value: string, maxAgeSec: number) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSec}; SameSite=Lax`;
}
function generateEventId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
function getOrCreateExternalId(): string {
  if (typeof window === "undefined") return "";
  const KEY = "lvbl_eid";
  let id = "";
  try { id = window.localStorage.getItem(KEY) || ""; } catch { /* ignore */ }
  if (!id) id = getCookie(KEY) || "";
  if (!id) {
    id = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  try { window.localStorage.setItem(KEY, id); } catch { /* ignore */ }
  setCookie(KEY, id, 60 * 60 * 24 * 365);
  return id;
}
async function getClickIdWithRetry(maxWaitMs = 1500): Promise<{ clickid: string; source: string }> {
  const start = Date.now();
  while (Date.now() - start < maxWaitMs) {
    const fromStorage = window.localStorage.getItem("clickid");
    if (fromStorage) return { clickid: fromStorage, source: "localStorage" };
    const btn = document.querySelector<HTMLAnchorElement>('a[href*="/click"]');
    if (btn?.href && btn.href.includes("clickid=")) {
      try {
        const u = new URL(btn.href);
        const c = u.searchParams.get("clickid");
        if (c) {
          window.localStorage.setItem("clickid", c);
          return { clickid: c, source: "button" };
        }
      } catch { /* ignore */ }
    }
    await new Promise((r) => setTimeout(r, 200));
  }
  return { clickid: "", source: "none" };
}

export const Route = createFileRoute("/miguelc")({
  head: () => ({
    meta: [
      { title: "Cura pela Alimentação — Masterclass ao vivo" },
      {
        name: "description",
        content:
          "Masterclass gratuita e ao vivo: descubra como ativar a capacidade natural de cura do seu corpo pela alimentação.",
      },
      { property: "og:title", content: "Cura pela Alimentação — Masterclass ao vivo" },
      {
        property: "og:description",
        content:
          "Aula gratuita ao vivo no sábado, 25/07 às 20h. Vagas limitadas, sem replay.",
      },
    ],
  }),
  component: Index,
});

function Index() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [eventLabel] = useState("Sábado, 25/07 às 20h");



  // Tracking bootstrap: track.js + clickid capture + PageView postback
  useEffect(() => {
    if (typeof window === "undefined") return;

    void import("@/lib/visitor-identity").then((m) => m.resolveFbClick());
    getOrCreateExternalId();

    const loadTrackingScript = (attempt = 1, max = 3): void => {
      const existing = document.querySelector('script[src*="kegqh.ttrk.io/track.js"]');
      if (existing) existing.remove();
      const s = document.createElement("script");
      s.src = `${TRACK_SCRIPT_BASE}?rtkcmpid=${TRACK_CMP_ID}&t=${Date.now()}`;
      s.async = true;
      s.onerror = () => {
        if (attempt < max) setTimeout(() => loadTrackingScript(attempt + 1, max), 2000);
      };
      document.head.appendChild(s);
    };
    const startLoad = () => {
      const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
      if (ric) ric(() => loadTrackingScript(), { timeout: 2000 });
      else window.setTimeout(() => loadTrackingScript(), 200);
    };
    if (document.readyState === "complete") startLoad();
    else window.addEventListener("load", startLoad, { once: true });

    let captured = false;
    const processAnchor = (a: HTMLAnchorElement) => {
      if (captured || !a.href || !a.href.includes("clickid=")) return;
      try {
        const u = new URL(a.href);
        const c = u.searchParams.get("clickid");
        if (c) {
          window.localStorage.setItem("clickid", c);
          captured = true;
        }
      } catch { /* ignore */ }
    };
    const scan = () => {
      if (captured) return;
      document.querySelectorAll<HTMLAnchorElement>('a[href*="/click"]').forEach(processAnchor);
    };
    scan();
    const obs = new MutationObserver(scan);
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["href"] });
    let n = 0;
    const scanInt = window.setInterval(() => {
      if (captured || ++n > 40) window.clearInterval(scanInt);
      else scan();
    }, 500);

    const pbInt = window.setInterval(() => {
      const c = window.localStorage.getItem("clickid");
      if (c) {
        fetch(`${PAGEVIEW_POSTBACK_BASE}?type=PageView&clickid=${encodeURIComponent(c)}`).catch(() => {});
        window.clearInterval(pbInt);
      }
    }, 500);

    return () => {
      document.querySelectorAll('script[src*="kegqh.ttrk.io/track.js"]').forEach((s) => s.remove());
      obs.disconnect();
      window.clearInterval(scanInt);
      window.clearInterval(pbInt);
    };
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (!valid) {
      setError("Digite um e-mail válido para garantir sua vaga.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      // 1) clickid: URL params -> retry storage
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get("clickid") || params.get("click_id") || params.get("xcod");
      let clickid = "";
      let clickidSource = "none";
      if (fromUrl) {
        clickid = fromUrl;
        clickidSource = "url";
        window.localStorage.setItem("clickid", fromUrl);
      } else {
        const got = await getClickIdWithRetry(1500);
        clickid = got.clickid;
        clickidSource = got.source;
      }

      // 2) Meta Pixel Lead com eventID p/ dedup com CAPI
      const eventId = generateEventId();
      const fbp = getCookie("_fbp");
      const { saveVisitorIdentity, resolveFbClick, applyAdvancedMatching } = await import(
        "@/lib/visitor-identity"
      );
      const fbClick = resolveFbClick();
      const fbc = fbClick.fbc;
      const externalId = getOrCreateExternalId();
      saveVisitorIdentity({ email: trimmed });
      applyAdvancedMatching();
      try {
        if (typeof window.fbq === "function") {
          window.fbq(
            "track",
            "Lead",
            {
              content_name: "Doutor Eduardo Corassa",
            },
            { eventID: eventId, external_id: externalId },
          );

        }
      } catch { /* ignore */ }

      // 3) Salva no backend (fire-and-forget com keepalive)
      fetch("/api/public/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmed,
          clickid: clickid || null,
          clickid_source: clickidSource !== "none" ? clickidSource : null,
          meta_event_id: eventId,
          fbp,
          fbc,
          external_id: externalId,
          fbclid: fbClick.fbclid,
        }),
        keepalive: true,
      }).catch((err) => console.error("[CAPTURA] erro fetch:", err));

      setSuccess(true);
      navigate({ to: "/obrigado" });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Não foi possível registrar sua inscrição. Tente novamente.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative flex min-h-screen items-start justify-center px-4 pt-2 pb-6 sm:items-center sm:py-12">
      {/* Anchor invisível usado pelo track.js para gerar clickid */}
      <a
        href="https://kegqh.ttrk.io/click"
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
      >
        <button type="button" tabIndex={-1}>Track</button>
      </a>

      {/* ambient glow */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-leaf/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[320px] w-[320px] rounded-full bg-leaf-bright/10 blur-3xl" />
      </div>

      <div className="relative z-10 flex w-full max-w-[390px] flex-col items-center">
        {success ? (
          <SuccessState />
        ) : (
          <>
            {/* Hero card */}
            <div className="relative mb-3 aspect-[16/9] w-full overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-leaf/20 to-deep sm:mb-6 sm:aspect-[4/3]">
              <img
                src={heroImage}
                alt="Palestrante nutricionista sorrindo no palco com microfone"
                width={832}
                height={704}
                className="absolute inset-0 h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-deep via-deep/30 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-end p-4 sm:p-6">
                <div className="mb-2 flex items-center gap-2">
                  <Leaf className="size-5 text-leaf-bright" strokeWidth={2} />
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-cream/70">
                    Masterclass
                  </span>
                </div>
                <h2 className="font-display text-2xl font-bold uppercase leading-none tracking-tight text-leaf-bright sm:text-3xl">
                  Cura pela
                  <br />
                  Alimentação
                </h2>
              </div>
            </div>

            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-leaf/40 bg-leaf/10 px-4 py-1.5 sm:mb-7">
              <span className="size-2 rounded-full bg-leaf-bright live-dot" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-cream">
                Ao vivo • {eventLabel}
              </span>
            </div>

            <h1 className="mb-3 text-center font-display text-[1.65rem] font-bold uppercase leading-[1.1] text-cream sm:mb-5 sm:text-3xl">
              E se a cura nunca esteve na farmácia?
            </h1>

            <p className="mb-4 px-2 text-center text-[13px] leading-relaxed text-cream/70 sm:mb-7 sm:text-sm">
              O seu corpo sabe se curar sozinho — isso rendeu um Nobel de
              Medicina em 2016. Nesta aula gratuita, você aprende a religar esse
              poder natural.
            </p>

            <form onSubmit={onSubmit} className="w-full space-y-3 sm:space-y-4" noValidate>
              <p className="text-center text-xs font-semibold uppercase tracking-wide text-gold">
                Preencha seus dados abaixo:
              </p>

              <div className="relative">
                <Mail
                  className="pointer-events-none absolute left-5 top-1/2 size-5 -translate-y-1/2 text-deep/40"
                  strokeWidth={2}
                />
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  maxLength={120}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder="Insira seu melhor e-mail"
                  aria-invalid={!!error}
                  aria-describedby={error ? "email-error" : undefined}
                  className="w-full rounded-2xl border-none bg-cream py-4 pl-14 pr-6 text-sm font-medium text-deep shadow-inner placeholder:text-deep/40 focus:outline-none focus:ring-2 focus:ring-leaf-bright sm:py-5"
                  style={{ color: "var(--deep)" }}
                />
              </div>

              {error && (
                <p
                  id="email-error"
                  className="text-center text-xs"
                  style={{ color: "oklch(0.85 0.12 30)" }}
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading || success}
                className="w-full rounded-2xl bg-leaf-bright py-4 text-base font-bold uppercase tracking-tight text-deep transition-all hover:brightness-110 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-80 sm:py-5 sm:text-lg"
                style={{
                  boxShadow: "0 10px 30px -5px oklch(0.72 0.2 140 / 0.4)",
                }}
              >
                {loading ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <Loader2 className="size-5 animate-spin" /> Enviando…
                  </span>
                ) : (
                  "Quero aprender a me curar!"
                )}
              </button>

              <p className="text-center text-[10px] tracking-tight text-cream/40">
                Vagas limitadas • Sem replay disponível — apenas ao vivo.
              </p>
            </form>
          </>
        )}
      </div>
    </main>
  );
}

function SuccessState() {
  return (
    <div className="flex flex-col items-center gap-5 px-6 py-12 text-center sm:px-8">
      <div className="relative">
        <div className="absolute inset-0 rounded-full bg-leaf-bright/30 blur-2xl" />
        <CheckCircle2
          className="relative size-16 text-leaf-bright"
          strokeWidth={1.5}
        />
      </div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gold">
        Inscrição confirmada
      </p>
      <h1 className="font-display text-3xl font-bold uppercase leading-tight text-cream">
        Te espero ao vivo
      </h1>
      <p className="max-w-[280px] text-sm leading-relaxed text-muted-foreground">
        Sua vaga na masterclass <span className="text-leaf-bright">Cura pela Alimentação</span> está garantida.
        Nos encontramos na <span className="text-cream font-semibold">sábado, às 20h</span>.
      </p>
      <span className="mt-2 inline-flex items-center gap-2 rounded-full border border-leaf/40 bg-leaf/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-cream">
        <span className="size-2 rounded-full bg-leaf-bright live-dot" />
        Ao vivo • Sábado, 25/07 às 20h
      </span>
    </div>
  );
}
